import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

// MX-local YYYY-MM-DD (uses UTC-6 approximation via Intl)
function mxDate(d) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(d);
}

function addDays(dateStr, days) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

export function presetRange(preset) {
  const today = mxDate(new Date());
  if (preset === "today") return { date_from: today, date_to: today };
  if (preset === "week") {
    const [y, m, d] = today.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    const dow = dt.getUTCDay(); // 0 Sun … 6 Sat
    const back = (dow + 6) % 7; // Monday-based
    const start = addDays(today, -back);
    return { date_from: start, date_to: today };
  }
  if (preset === "month") {
    const start = today.slice(0, 8) + "01";
    return { date_from: start, date_to: today };
  }
  return { date_from: today, date_to: today };
}

export function DateRangeFilter({ value, onChange, showCustom = true }) {
  const preset = value?.preset || "month";

  const setPreset = (p) => {
    const r = presetRange(p);
    onChange({ preset: p, ...r });
  };

  const setDate = (key, v) => {
    onChange({ ...value, preset: "custom", [key]: v });
  };

  const presets = useMemo(
    () => [
      { key: "today", label: "Hoy" },
      { key: "week", label: "Semana" },
      { key: "month", label: "Mes" },
    ],
    []
  );

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3" data-testid="date-range-filter">
      <div className="flex flex-wrap items-center gap-2">
        {presets.map((p) => (
          <Button
            key={p.key}
            variant={preset === p.key ? "default" : "outline"}
            size="sm"
            className={preset === p.key ? "bg-tenant hover:bg-tenant/90 text-white" : ""}
            data-testid={`date-preset-${p.key}`}
            onClick={() => setPreset(p.key)}
          >
            {p.label}
          </Button>
        ))}
      </div>
      {showCustom && (
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Desde
            </Label>
            <Input
              type="date"
              value={value?.date_from || ""}
              onChange={(e) => setDate("date_from", e.target.value)}
              className="h-9 w-40"
              data-testid="date-from"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Hasta
            </Label>
            <Input
              type="date"
              value={value?.date_to || ""}
              onChange={(e) => setDate("date_to", e.target.value)}
              className="h-9 w-40"
              data-testid="date-to"
            />
          </div>
        </div>
      )}
    </div>
  );
}
